/**
 * ReviewNudge — the manual daily nudge for pulling planned reviews into
 * the pipeline (CLAUDE.md §34.9 / §34.10). One presentation over the
 * computeReviewNudge pacing:
 *
 *   <ReviewNudgeBanner />  — full banner on the Reviews planner page
 *
 * (A compact one-line version on the Adviser dashboard was removed on
 * 27 October 2026 to declutter the dashboard.)
 *
 * The nudge counts review MEETINGS (units): a group of clients seen
 * together counts one, an ungrouped client counts one. Pulling a group
 * unit transitions every member (one calendar card).
 *
 * MANUAL ONLY: nothing here schedules or automates anything. The nudge
 * suggests a sustainable daily rate; the Adviser clicks "Add to
 * pipeline" per unit (repeatedly, when the pacing says so). This
 * component performs no writes itself — the parent's onAddToPipeline
 * receives the unit descriptor ({ memberIds, label, isGroup, … }).
 */

import React from 'react';
import { CalendarCheck, ArrowRight } from 'lucide-react';
import {
  MONTH_NAMES,
  computeReviewNudge,
  getCurrentSASTYearMonth,
} from '../../utils/reviewPlanner';
import '../../styles/reviews.css';

/** "Botes (group of 3)" for a group unit; the natural name otherwise. */
function unitDisplay(unit) {
  return unit.isGroup ? `${unit.label} (group of ${unit.memberCount})` : unit.displayName;
}

/**
 * Full banner for the Reviews page. `clients` must be the ACTIVE
 * (non-completed) review clients across years, so the current-month
 * remaining count and the earlier-months overdue count are both right
 * regardless of which planner year is being viewed.
 */
export function ReviewNudgeBanner({ clients, groupLabels = {}, onAddToPipeline, busy = false }) {
  const now = getCurrentSASTYearMonth();
  const nudge = computeReviewNudge(clients, now, groupLabels);

  if (nudge.remaining === 0 && nudge.overdueCount === 0) {
    return (
      <div className="rv-nudge rv-nudge-quiet">
        <CalendarCheck size={16} />
        <span>All caught up — no review meetings waiting to be pulled this month.</span>
      </div>
    );
  }

  return (
    <div className="rv-nudge">
      <CalendarCheck size={18} className="rv-nudge-icon" />
      <div className="rv-nudge-body">
        <div className="rv-nudge-title">Today's reviews — {MONTH_NAMES[now.month]}</div>
        <div className="rv-nudge-line">
          {nudge.remaining} review meeting{nudge.remaining === 1 ? '' : 's'} not yet in the
          pipeline · {nudge.workingDaysLeft} working day
          {nudge.workingDaysLeft === 1 ? '' : 's'} left · suggested today:{' '}
          <strong>{nudge.suggestedToday}</strong>
        </div>
        {nudge.overdueCount > 0 && (
          <div className="rv-nudge-overdue">
            + {nudge.overdueCount} overdue from earlier months
          </div>
        )}
      </div>
      {nudge.nextUnit && (
        <div className="rv-nudge-next">
          <span className="rv-nudge-next-label">Next:</span>
          {/* Natural name — the nudge is not a planner list surface. */}
          <span className="rv-nudge-next-name">{unitDisplay(nudge.nextUnit)}</span>
          <button
            className="rv-btn rv-btn-primary"
            onClick={() => onAddToPipeline(nudge.nextUnit)}
            disabled={busy}
          >
            Add to pipeline <ArrowRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
